import Quill from 'quill';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormError } from 'src/ui/shared/hooks/form-error.hook';
import { HookControlProps } from '../../hook-form.models';
import { ControlFormGroup } from '../../shared/ControlFormGroup';
import { HtmlControlMaxLength } from './HtmlControlMaxLength';

const bold = Quill.import('formats/bold') as any;
bold.tagName = 'b'; // Quill uses <strong> by default
Quill.register(bold, true);

const italic = Quill.import('formats/italic') as any;
italic.tagName = 'i'; // Quill uses <em> by default
Quill.register(italic, true);

const onlyNewLines = new RegExp('[\\n]+$');

interface Props extends HookControlProps {
    htmlContentMaxLength?: number;
}

export const HtmlControl2 = (props: Props): React.ReactElement => {
    const { name, placeholder, rules, htmlContentMaxLength } = props;

    const [loaded, setLoaded] = useState(false);
    const ref = useRef<any>(null);
    const htmlRef = useRef<HTMLDivElement>(null);

    const { register, getValues, setValue, trigger } = useFormContext();
    const controlError = useFormError(name);

    const [length, setLength] = useState(0);
    const [hasFocus, setHasFocus] = useState(false);

    const validateMaxLength = useCallback(() => {
        if (!htmlContentMaxLength) {
            return undefined;
        }

        if (length > htmlContentMaxLength) {
            console.log('legnth', length);
            return `This field must be ${htmlContentMaxLength} characters or less.`;
        }
        return undefined;
    }, [htmlContentMaxLength, length]);

    useEffect(() => {
        register(name, {
            ...rules,
            validate: validateMaxLength,
        });
    }, [htmlContentMaxLength, length, name, register, rules, validateMaxLength]);

    useEffect(() => {
        if (!Quill || loaded || !htmlRef) {
            return;
        }

        const divQuerySelector = `div[data-name="html-${name}"]`;

        const quill = new Quill(divQuerySelector, {
            theme: 'snow',
            placeholder,
            // prettier-ignore
            formats: [
                // inline
                'bold', 'italic', 'link',
                // block
                'header', 'list',
            ],
            modules: {
                // prettier-ignore
                toolbar: [
                    [{ 'header': 5 }, 'bold', 'italic', 'link'],
                    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                    ['clean']
                ],
            },
        });

        quill.on(Quill.events.SELECTION_CHANGE, (range, oldRange) => {
            if (range === null) {
                setHasFocus(false);
            }

            if (oldRange === null) {
                setHasFocus(true);
            }
        });

        quill.on(Quill.events.TEXT_CHANGE, () => {
            const htmlLength = quill.getLength();

            // quill always has a \n character so it's never 0
            if (htmlLength === 1) {
                setLength(0);
                setValue(name, '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
                return;
            }

            const contentDelta = quill.getContents();
            const textLength = quill.getText().length;
            const listItems = contentDelta.ops
                .filter(op => !!op.insert)
                .filter(op => op.attributes?.list === 'bullet' || op.attributes?.list === 'ordered')
                .filter(op => typeof op.insert === 'string' && onlyNewLines.test(op.insert))
                .map(op => op.insert! as string)
                .join('').length;

            const listLength = listItems * 2;
            const calculatedLength = textLength + listLength;
            setLength(calculatedLength);

            // this is a known issue which hasn't been fixed
            // which is pretty concerning
            // https://github.com/slab/quill/issues/4509
            const html = quill.getSemanticHTML().replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ');

            // probably inefficent, doesn't really need to set anything until blur or submit
            setValue(name, html, { shouldDirty: true, shouldTouch: true });
        });

        // on paste, change all headers into H5, as it's all we allow through the editor
        ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].forEach(tag => {
            quill.clipboard.addMatcher(tag, (node, delta) => {
                delta.ops.forEach(op => {
                    if (!op.attributes) {
                        op.attributes = {};
                    }
                    op.attributes.header = 5;
                });
                return delta;
            });
        });

        // set initial value if it exists
        const initialValue = getValues(name);
        if (initialValue) {
            const delta = quill.clipboard.convert({ html: initialValue });
            quill.setContents(delta, 'silent');
        }

        ref.current = quill;

        setLoaded(true);
    }, [getValues, loaded, name, placeholder, setValue]);

    return (
        <ControlFormGroup
            {...props}
            maxLengthOverride={
                <HtmlControlMaxLength
                    length={length}
                    maxLength={htmlContentMaxLength}
                    hasFocus={hasFocus}
                />
            }
        >
            <div
                // ToDo: fix up border
                className={controlError ? 'border border-danger' : ''}
                onBlur={() => void trigger(name)}
            >
                <div data-name={`html-${name}`} ref={htmlRef}></div>
            </div>
        </ControlFormGroup>
    );
};
